import React, { useCallback, useEffect, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { Flipped, Flipper, spring } from 'react-flip-toolkit';
import { connect } from 'unistore/full/react';
import actions from './actions';
import { DeferredLoadingIcon } from './DeferredLoadingIcon';
import NavigationBar from './FigmaComponents/NavigationBar';
import getImagesUrlBySize from './get-images-url-by-size';
import { LoadingIcon } from './LoadingIcon';
import './PlaylistTagRadio.scss';
import { Scroller } from './Scroller';
import TrackRow from './TrackRow';
import TrackRowActionsWrapper from './TrackRowActionsWrapper';

const onAppear = (el, index) =>
  spring({
    onUpdate: (val) => {
      el.style.opacity = val;
    },
  });

const onExit = (el, index, removeElement) => {
  spring({
    config: { overshootClamping: true },
    onUpdate: (val) => {
      el.style.opacity = 1 - val;
    },
    onComplete: removeElement,
  });

  return () => {
    el.style.opacity = '';
    removeElement();
  };
};

function PlaylistTagRadio(props) {
  const {
    playlistId,
    name,
    genre,
    color,
    imageUrl,
    currentRow,
    error,
    rows,
    addedStateByUri,
    playlistUri,
    isActiveContext,
    onDemand,
    isPreviewPlayerPlaying,
    previewUri,
    isSameFeatureIdentifier,
    isLoaded,
  } = props;

  useEffect(() => {
    props.fetchPlaylistTagRadio(playlistId, genre);
  }, [playlistId]);

  const onClickPlayButton = useCallback(() => {
    isActiveContext && isSameFeatureIdentifier
      ? props.togglePlayback()
      : props.playTracksFromUri({
          rows,
          contextUri: playlistUri,
        });
  }, [rows, playlistUri, isActiveContext, isSameFeatureIdentifier]);

  return (
    <>
      <Scroller
        sectionId={`playlist-tag-radio-${playlistId}`}
        scrollId={genre}
        className="Playlist__scroller"
      >
        <div>
          <NavigationBar />

          <div className="Playlist">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              fill="none"
              viewBox="0 0 375 432"
            >
              <g>
                <path fill="#121212" d="M0 0H375V492H0z"></path>
                <path
                  fill="#318394"
                  d="M0 0H375V432H0z"
                  style={{
                    transition: 'fill .3s ease-in-out',
                    fill: color || '#606060',
                  }}
                ></path>
                <path
                  fill="url(#radio-gradient-overlay)"
                  d="M0 0H375V432H0z"
                ></path>
                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="24"
                  fontWeight="bold"
                  letterSpacing="-.04em"
                  textAnchor="middle"
                >
                  <tspan x={135.488 + 104.03 / 2} y="247.86">
                    {name}
                  </tspan>
                </text>
                <circle
                  cx="188"
                  cy="119"
                  r="101"
                  fill="url(#radio-circle-inner)"
                  transform="rotate(-90 188 119)"
                ></circle>
                <path
                  fill="url(#radio-circle-outer)"
                  d="M188-18c75.663 0 137 61.337 137 137s-61.337 137-137 137S51 194.663 51 119 112.337-18 188-18z"
                ></path>

                <g onClick={onClickPlayButton}>
                  <path
                    fill="#1ED760"
                    d="M119 324c0-13.255 10.745-24 24-24h90c13.255 0 24 10.745 24 24s-10.745 24-24 24h-90c-13.255 0-24-10.745-24-24z"
                  ></path>
                  <text
                    fill="#000"
                    style={{ whiteSpace: 'pre' }}
                    fontSize="16"
                    fontWeight="bold"
                    letterSpacing="0em"
                  >
                    <tspan x="151.453" y="329.24">
                      Play radio
                    </tspan>
                  </text>
                </g>

                <text
                  fill="#B0B1B1"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="10"
                  fontWeight="bold"
                  letterSpacing=".1em"
                >
                  <tspan x="139.278" y="275.275">
                    PLAYLIST RADIO
                  </tspan>
                </text>

                <text
                  fill="#fff"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="24"
                  fontWeight="bold"
                  letterSpacing="-.04em"
                >
                  <tspan x="16" y="403.86">
                    In this station
                  </tspan>
                </text>
              </g>

              <rect x="129" y="58" width="122" height="122" fill="#242424" />

              <g
                id="radio-fallback-icon"
                fill="#A7A7A7"
                transform="translate(166, 95)"
              >
                <path d="M14.8749 31.794C10.5779 26.762 11.1759 19.173 16.2069 14.875C16.6129 14.528 17.0499 14.219 17.4989 13.929L16.1869 12.393C15.7439 12.692 15.3129 13.008 14.9079 13.355C9.03791 18.368 8.34091 27.223 13.3529 33.093C14.1739 34.054 15.1259 34.894 16.1889 35.605L17.5029 34.067C16.5109 33.431 15.6259 32.673 14.8749 31.794Z" />
                <path d="M33.1249 16.206C37.4219 21.238 36.8239 28.828 31.7919 33.125C31.3879 33.471 30.9509 33.78 30.5009 34.071L31.8129 35.607C32.2569 35.308 32.6879 34.991 33.0919 34.645C38.9619 29.632 39.6589 20.778 34.6469 14.907C33.8259 13.946 32.8739 13.105 31.8109 12.394L30.4969 13.933C31.4879 14.569 32.3739 15.327 33.1249 16.206Z" />
                <path d="M23.9999 18C20.6909 18 17.9999 20.691 17.9999 24C17.9999 27.309 20.6909 30 23.9999 30C27.3089 30 29.9999 27.309 29.9999 24C29.9999 20.691 27.3089 18 23.9999 18ZM23.9999 28C21.7939 28 19.9999 26.206 19.9999 24C19.9999 21.794 21.7939 20 23.9999 20C26.2059 20 27.9999 21.794 27.9999 24C27.9999 26.206 26.2059 28 23.9999 28Z" />
                <path d="M12.2819 40.18C10.9839 39.243 9.80691 38.178 8.79191 36.989C1.62991 28.603 2.62491 15.954 11.0109 8.79199C11.3929 8.46499 11.8089 8.14499 12.2769 7.81299L10.9739 6.28699C10.5159 6.61699 10.0999 6.93999 9.71191 7.27199C5.24291 11.088 2.52791 16.417 2.06791 22.274C1.60691 28.132 3.45491 33.819 7.27191 38.289C8.35591 39.558 9.60491 40.699 10.9789 41.706L12.2819 40.18Z" />
                <path d="M40.7289 9.71199C39.6449 8.44299 38.3959 7.30199 37.0209 6.29399L35.7179 7.81999C37.0169 8.75799 38.1939 9.82299 39.2079 11.011C42.6779 15.073 44.3569 20.244 43.9379 25.569C43.5189 30.895 41.0509 35.739 36.9889 39.208C36.6049 39.536 36.1889 39.856 35.7229 40.187L37.0259 41.713C37.4809 41.384 37.8979 41.061 38.2869 40.729C42.7559 36.913 45.4709 31.584 45.9309 25.726C46.3939 19.868 44.5459 14.181 40.7289 9.71199Z" />
              </g>

              <image
                x="129"
                y="58"
                width="122"
                height="122"
                xlinkHref={imageUrl}
              />

              <defs>
                <linearGradient
                  id="radio-gradient-overlay"
                  x1="187.5"
                  x2="187.5"
                  y1="0"
                  y2="432"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#121212" stopOpacity="0"></stop>
                  <stop offset="1" stopColor="#121212"></stop>
                </linearGradient>
                <linearGradient
                  id="radio-circle-inner"
                  x1="188"
                  x2="188"
                  y1="18"
                  y2="220"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#C4C4C4" stopOpacity="0.2"></stop>
                  <stop
                    offset="0.25"
                    stopColor="#C4C4C4"
                    stopOpacity="0"
                  ></stop>
                  <stop
                    offset="0.755"
                    stopColor="#C4C4C4"
                    stopOpacity="0"
                  ></stop>
                  <stop offset="1" stopColor="#C4C4C4" stopOpacity="0.2"></stop>
                </linearGradient>
                <linearGradient
                  id="radio-circle-outer"
                  x1="51"
                  x2="325"
                  y1="119"
                  y2="119"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#C4C4C4" stopOpacity="0.2"></stop>
                  <stop
                    offset="0.25"
                    stopColor="#C4C4C4"
                    stopOpacity="0"
                  ></stop>
                  <stop
                    offset="0.755"
                    stopColor="#C4C4C4"
                    stopOpacity="0"
                  ></stop>
                  <stop offset="1" stopColor="#C4C4C4" stopOpacity="0.2"></stop>
                </linearGradient>
              </defs>
            </svg>

            <AutoSizingInThisStation
              artists={Array.from(
                new Set(rows?.map((row) => row?.track?.artists?.[0]?.name))
              ).slice(0, 10)}
            />

            {error && <div>{error}</div>}

            {isLoaded ? null : (
              <div className="PlaylistTagRadio__loading-icon-wrapper">
                <DeferredLoadingIcon />
              </div>
            )}

            <div className="Playlist__tracklist-wrapper">
              <Flipper
                flipKey={rows?.map(({ uid }) => uid)?.join('')}
                spring="noWobble"
              >
                {rows?.map(
                  (
                    {
                      track: {
                        uri,
                        id,
                        name,
                        album: { images = [] } = {},
                        artists,
                        sortValue,
                      } = {},
                      uid = uri,
                      metadata: { source } = {},
                    },
                    index
                  ) => {
                    const isAdded = addedStateByUri[uri];
                    return (
                      <React.Fragment key={uid}>
                        <Flipped
                          key={uid}
                          flipId={uid}
                          onAppear={onAppear}
                          onExit={onExit}
                        >
                          <div className="Playlist__track-row-wrapper">
                            <TrackRowActionsWrapper
                              onAddRemove={() => {
                                setTimeout(
                                  () =>
                                    props.toggleSavedStateForUri(
                                      uri,
                                      playlistUri
                                    ),
                                  1000
                                );
                              }}
                              isSaved={isAdded}
                            >
                              <TrackRow
                                uri={uri}
                                title={name}
                                subtitle={artists
                                  .map(({ name }) => name)
                                  .join(', ')}
                                imageUrl={getImagesUrlBySize(images, 54)}
                                onClick={() =>
                                  onDemand
                                    ? props.playTracksFromUri({
                                        trackUri: uri,
                                        rows,
                                        index,
                                        contextUri: playlistUri,
                                      })
                                    : isPreviewPlayerPlaying &&
                                      previewUri === uri
                                    ? props.pausePreview(uri)
                                    : props.playPreview(uri)
                                }
                                isPlaying={
                                  uri &&
                                  uri ===
                                    (currentRow &&
                                      currentRow.track &&
                                      currentRow.track.uri) &&
                                  isActiveContext &&
                                  isSameFeatureIdentifier
                                }
                                onToggleFeedbackState={() => {
                                  // props.feedbackTrack(uri, playlistUri, 0);
                                }}
                                onActionButtonClick={() =>
                                  props.openContextMenu(uri, playlistUri)
                                }
                                onDemand={onDemand}
                                sortValue={sortValue}
                              />
                            </TrackRowActionsWrapper>
                          </div>
                        </Flipped>
                      </React.Fragment>
                    );
                  }
                )}
              </Flipper>
            </div>
          </div>
        </div>
      </Scroller>
    </>
  );
}

export default connect(
  (
    {
      currentRow,
      contextUri,
      addedStateByUri,
      contextPlayerState,
      playerState: { paused } = {},
      onDemand,
      preview: { uri: previewUri, isPlaying: isPreviewPlayerPlaying },
      featureIdentifier,
      stationsByIdAndSteering,
    },
    { match: { params: { id: playlistId = '', genre = '' } = {} } = {} }
  ) => {
    const station = stationsByIdAndSteering[`${playlistId}${genre}`];

    const playlistUri = `spotify:playlist:${playlistId}`;
    const isActiveContext = playlistUri === contextUri;
    return {
      playlistId,
      genre,
      currentRow,
      contextUri,
      addedStateByUri,
      playlistUri,
      isPlaying: !paused && playlistUri === contextUri,
      isSameFeatureIdentifier:
        featureIdentifier ===
        contextPlayerState?.play_origin?.feature_identifier,
      isActiveContext,
      onDemand,
      isPreviewPlayerPlaying,
      previewUri,
      ...station,
    };
  },
  actions
)(React.memo(PlaylistTagRadio, isEqual));

const AutoSizingInThisStation = React.memo(function AutoSizingInThisStation({
  artists,
}) {
  const lineCount = 2;
  const lineHeight = 24;

  const autoSizingRef = useRef(null);

  const render = (artists, andMore) =>
    `<div class="PlaylistTagRadio__in-this-station">
      ${escapeHTML(artists.join(', '))}${andMore ? ' and more' : ''}
  </div>`;

  useEffect(() => {
    if (!artists?.length) return;

    const artistsToRender = artists.concat();

    let hasDiscardedArtists = false;
    do {
      const html = render(artistsToRender, hasDiscardedArtists);
      autoSizingRef.current.innerHTML = html;

      if (autoSizingRef.current?.offsetHeight <= lineCount * lineHeight) {
        break;
      } else {
        hasDiscardedArtists = true;
      }
    } while (artistsToRender.pop());
  }, [artists]);

  return artists?.length ? (
    <div
      className="PlaylistTagRadio__in-this-station-wrapper"
      ref={autoSizingRef}
    />
  ) : null;
});

const escapeHTML = (str) =>
  str.replace(
    /[&<>'"]/g,
    (tag) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
      }[tag] || tag)
  );
